import type { PromoCode } from './codes'

/**
 * The sign-up form that earns the promo, which every promo component opens.
 * The form is the site's, not ours: a modal built in Webflow, whose form the
 * page's own code posts to Klaviyo, and which activates the promo once
 * Klaviyo has the email. A component only opens the modal.
 *
 * Webflow opens it with an interaction on a click, "Contact modal 5 [Open]",
 * which can't be started from code. So a component does what that
 * interaction does, to the same elements and with the same inline styles,
 * and the modal's own close button and backdrop, whose interaction resets
 * those styles, close it as they close any other opening.
 */

/** The site's sign-up modal, which every promo component opens unless told otherwise. */
export const DEFAULT_PROMO_MODAL = '.contact-modal5_component'

/** Webflow's `outQuad`, which slides the modal's content in. */
const OUT_QUAD = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'

/** How long the modal takes to slide and fade in, as Webflow's interaction does. */
const OPEN_MS = 500

/**
 * The modal to open for `code`, from whatever the hand-written attribute
 * delivered: none for the site's own, one selector for every code, or one per
 * code as JSON. Undefined when a per-code map names none for `code`.
 */
export function modalFor(modal: unknown, code: PromoCode): string | undefined {
  if (modal === undefined || modal === true) return DEFAULT_PROMO_MODAL
  const named =
    modal !== null && typeof modal === 'object' ? (modal as Record<string, unknown>)[code] : modal
  return typeof named === 'string' && named.trim() !== '' ? named.trim() : undefined
}

/**
 * Open the modal `data-modal` names for `code`, or the site's own without it.
 * With no modal on the page, or none named for the code, nothing opens, and
 * the console says which component missed it.
 */
export function openPromoModal(modal: unknown, code: PromoCode, component: string): void {
  const selector = modalFor(modal, code)
  if (!selector) {
    console.warn(`[codeverse] ${component} has no data-modal for ${code}`)
    return
  }
  const element = findModal(selector)
  if (!element) {
    console.warn(`[codeverse] ${component} found no ${selector} on the page`)
    return
  }
  showModal(element)
}

function findModal(selector: string): HTMLElement | null {
  try {
    return document.querySelector<HTMLElement>(selector)
  } catch {
    // A hand-typed selector that isn't one finds nothing, rather than throwing in a click.
    return null
  }
}

/**
 * What Webflow's "[Open]" interaction does to one of its modals, named the
 * way its modals name their parts: `contact-modal5_component` shows, as a
 * flexbox, its `contact-modal5_content-wrapper` slides up from below, and its
 * `contact-modal5_background-overlay` fades in. The final styles go inline,
 * where Webflow's close interaction finds and resets them; the animation only
 * runs on top of them.
 */
function showModal(element: HTMLElement): void {
  element.style.display = 'flex'

  const part = [...element.classList].find((name) => name.endsWith('_component'))
  if (!part) return
  const prefix = part.slice(0, -'_component'.length)

  const content = element.querySelector<HTMLElement>(`.${prefix}_content-wrapper`)
  if (content) {
    content.style.transform = 'translate3d(0px, 0%, 0px)'
    content.animate?.(
      [{ transform: 'translate3d(0px, 100%, 0px)' }, { transform: 'translate3d(0px, 0%, 0px)' }],
      { duration: OPEN_MS, easing: OUT_QUAD },
    )
  }

  const overlay = element.querySelector<HTMLElement>(`.${prefix}_background-overlay`)
  if (overlay) {
    overlay.style.opacity = '1'
    overlay.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: OPEN_MS, easing: 'ease' })
  }
}
